import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Camera,
  ShieldCheck,
  Heart,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Save,
} from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isComplete = (parent, children) => {
  if (!parent?.photo_url) return false;
  if (!children || children.length === 0) return false;
  return children.every(
    (c) =>
      (c.photo_url || "").trim() &&
      (c.allergies || "").trim() &&
      (c.soothing_techniques || "").trim()
  );
};

const PortalIntake = () => {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const eventSlug = sp.get("event") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [event, setEvent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/portal/verify`, {
        params: { token },
      });
      setParent(data.parent);
      setChildren(data.children || []);
      setEvent(data.event);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "This link is invalid or has expired. Please contact your caregiver."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError("Missing access token.");
      setLoading(false);
      return;
    }
    load();
  }, [token, load]);

  const updateParent = (k, v) => setParent((p) => ({ ...p, [k]: v }));
  const updateChild = (idx, k, v) =>
    setChildren((cs) => cs.map((c, i) => (i === idx ? { ...c, [k]: v } : c)));

  const save = async () => {
    setSaving(true);
    try {
      // Parent
      await axios.patch(
        `${API}/portal/parent`,
        {
          photo_url: parent.photo_url || "",
          notes: parent.notes || "",
        },
        { params: { token } }
      );
      // Each child
      for (const c of children) {
        await axios.patch(
          `${API}/portal/child/${c.id}`,
          {
            photo_url: c.photo_url || "",
            allergies: c.allergies || "",
            medical_notes: c.medical_notes || "",
            soothing_techniques: c.soothing_techniques || "",
            favorite_activities: c.favorite_activities || "",
          },
          { params: { token } }
        );
      }
      toast.success("Profile saved. You're all set for check-in.");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not save. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading your check-in…</div>;
  }
  if (error) {
    return (
      <main data-testid="portal-error" className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
        <AlertTriangle size={40} className="text-primary mb-4" />
        <h1 className="font-serif-display text-3xl text-foreground mb-3">Link unavailable</h1>
        <p className="max-w-md text-muted-foreground">{error}</p>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 text-primary text-sm">
          <ArrowLeft size={14} /> Back to Care Anywhere
        </Link>
      </main>
    );
  }

  const complete = isComplete(parent, children);

  return (
    <main data-testid="portal-intake" className="min-h-screen bg-background pb-24">
      {/* header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display">C</span>
            <span className="font-serif-display text-lg">Care Anywhere</span>
          </Link>
          {complete ? (
            <Badge data-testid="badge-complete" className="bg-primary text-primary-foreground rounded-full">Express Pass Active</Badge>
          ) : (
            <Badge data-testid="badge-incomplete" variant="secondary" className="rounded-full">Action Required</Badge>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-8 pt-10">
        <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Express Check-In</div>
        <h1 className="mt-3 font-serif-display text-3xl md:text-4xl leading-tight text-foreground">
          Hello {parent.first_name}, let's finalise your check-in.
        </h1>
        {event && (
          <p className="mt-3 text-muted-foreground">
            <strong className="text-foreground">{event.name}</strong>
            {" · "}
            {event.venue}
            {" · "}
            {format(new Date(event.start_at), "EEEE, MMMM d 'at' p")}
          </p>
        )}

        {/* Status card */}
        <div
          data-testid="status-card"
          className={`mt-8 rounded-2xl p-6 md:p-7 border ${
            complete
              ? "bg-primary/5 border-primary/30"
              : "bg-accent/10 border-accent/40"
          }`}
        >
          <div className="flex items-start gap-4">
            {complete ? (
              <CheckCircle2 size={28} className="text-primary shrink-0" />
            ) : (
              <AlertTriangle size={28} className="text-accent-foreground shrink-0" />
            )}
            <div>
              <div className="font-serif-display text-xl text-foreground">
                {complete ? "You're all set." : "A few details left."}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {complete
                  ? "Your profile is complete and verified. Your phone is your contactless entry pass."
                  : "Please add photos, allergies, and soothing techniques below to activate your Express Pass."}
              </p>
            </div>
          </div>
        </div>

        {/* Parent card */}
        <section data-testid="parent-card" className="mt-10 rounded-2xl border border-border/70 bg-card p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck size={18} className="text-primary" />
            </div>
            <div>
              <div className="font-serif-display text-xl">Your details</div>
              <div className="text-xs text-muted-foreground">Used for door-side visual verification.</div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <FieldWithPreview
              label="Your photo URL"
              testid="input-parent-photo"
              value={parent.photo_url || ""}
              placeholder="https://..."
              onChange={(v) => updateParent("photo_url", v)}
            />
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contact email</Label>
              <Input value={parent.email} readOnly className="mt-2 h-11 rounded-lg bg-muted" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes to the care team</Label>
              <Textarea
                data-testid="input-parent-notes"
                rows={3}
                value={parent.notes || ""}
                onChange={(e) => updateParent("notes", e.target.value)}
                placeholder="Anything about you or your pickup preferences..."
                className="mt-2 rounded-lg resize-none"
              />
            </div>
          </div>
        </section>

        {/* Children */}
        {children.map((c, idx) => (
          <section key={c.id} data-testid={`child-card-${idx}`} className="mt-8 rounded-2xl border border-border/70 bg-card p-6 md:p-8">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center">
                <Heart size={18} className="text-primary" />
              </div>
              <div>
                <div className="font-serif-display text-xl">{c.first_name}</div>
                <div className="text-xs text-muted-foreground">Age {c.age_years}</div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <FieldWithPreview
                label={`${c.first_name}'s photo URL`}
                testid={`input-child-photo-${idx}`}
                value={c.photo_url || ""}
                placeholder="https://..."
                onChange={(v) => updateChild(idx, "photo_url", v)}
              />
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <Sparkles size={12} className="text-primary" /> Allergies / medical alerts
                </Label>
                <Input
                  data-testid={`input-child-allergies-${idx}`}
                  value={c.allergies || ""}
                  onChange={(e) => updateChild(idx, "allergies", e.target.value)}
                  placeholder="e.g. peanuts (mild), lactose intolerant"
                  className="mt-2 h-11 rounded-lg"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Soothing techniques</Label>
                <Textarea
                  data-testid={`input-child-soothing-${idx}`}
                  rows={2}
                  value={c.soothing_techniques || ""}
                  onChange={(e) => updateChild(idx, "soothing_techniques", e.target.value)}
                  placeholder="What helps them settle? Songs, cuddle blankets, quiet corner..."
                  className="mt-2 rounded-lg resize-none"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Favorite activities</Label>
                <Textarea
                  data-testid={`input-child-fav-${idx}`}
                  rows={2}
                  value={c.favorite_activities || ""}
                  onChange={(e) => updateChild(idx, "favorite_activities", e.target.value)}
                  placeholder="Legos, drawing, dinosaurs..."
                  className="mt-2 rounded-lg resize-none"
                />
              </div>
            </div>
          </section>
        ))}

        {/* Save */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            This link is unique to you and expires in 7 days.
          </p>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="portal-save"
            className="rounded-full h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground min-w-[220px]"
          >
            {saving ? "Saving…" : <span className="flex items-center gap-2">Save & activate <Save size={16} /></span>}
          </Button>
        </div>
      </div>
    </main>
  );
};

const FieldWithPreview = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
      <Camera size={12} className="text-primary" /> {label}
    </Label>
    <div className="mt-2 flex items-center gap-3">
      {value ? (
        <img
          src={value}
          alt="Uploaded photo"
          className="h-11 w-11 rounded-full object-cover border border-border shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="h-11 w-11 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
          <Camera size={16} />
        </div>
      )}
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 rounded-lg"
      />
    </div>
  </div>
);

export default PortalIntake;
